import { BUILD_SHA } from '@/config/build';

/**
 * SAMONAPRAWA PO WDROŻENIU: stara karta w tle nie ma pokazywać zasłony „Coś poszło nie tak".
 *
 * CO SIĘ DZIEJE (zgłoszenie Piotra 27.09, zrzut z Safari). Człowiek zostawia otwartą kartę,
 * my wdrażamy cztery razy, on wraca do karty i klika. Aplikacja z pamięci karty próbuje
 * dociągnąć swój leniwy kawałek (`import()` dzielony po trasach) spod adresu z HASZEM STAREGO
 * BUILDU — a tego pliku już nie ma, bo wdrożenie zostawia tylko nowe. `import()` odrzuca,
 * React łapie to jako błąd renderu i pokazuje zasłonę. Z punktu widzenia człowieka strona
 * „się zepsuła", choć wszystko działa — tylko on ma w ręku aplikację sprzed czterech wdrożeń.
 *
 * DWA MECHANIZMY, BO TO DWA RÓŻNE MOMENTY:
 *  1. gdy błąd JUŻ padł — przeładowujemy stronę RAZ i wtedy wszystko wczytuje się z nowego
 *     buildu. Raz, nie w kółko: znacznik w `sessionStorage` niesie SHA buildu, który próbował
 *     się naprawić. Jeśli po przeładowaniu ten sam build znowu nie umie wczytać modułu, to nie
 *     jest już stara karta, tylko prawdziwa awaria — wtedy zasłona jest właściwą odpowiedzią.
 *  2. zanim błąd padnie — przy powrocie do karty sprawdzamy, czy serwer ma inny build, i jeśli
 *     tak, przeładowujemy przy NAJBLIŻSZEJ nawigacji. Ciche, bo człowiek i tak w tej chwili
 *     zmienia stronę; przeładowanie pod palcem w środku czytania byłoby gorsze niż problem.
 *
 * `sessionStorage` bywa niedostępne (tryb prywatny, zablokowane ciasteczka) — wtedy NIE
 * przeładowujemy w ogóle. Lepiej pokazać zasłonę niż wpaść w pętlę przeładowań, której nie
 * mamy czym przerwać.
 */
export const KLUCZ_SAMONAPRAWY = 'flh-samonaprawa-build';

/** Komunikaty, którymi przeglądarki mówią „nie ma tego kawałka aplikacji". */
const WZORCE = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

export function czyBladWczytaniaModulu(blad: unknown): boolean {
  if (!blad) return false;
  const tekst =
    typeof blad === 'string'
      ? blad
      : `${(blad as { name?: string }).name ?? ''} ${(blad as { message?: string }).message ?? ''}`;
  return WZORCE.some((w) => w.test(tekst));
}

interface Magazyn {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

function magazyn(): Magazyn | null {
  try {
    const s = globalThis.sessionStorage;
    // Sam dostęp potrafi rzucić (zablokowane dane witryny), więc próbujemy też zapisu.
    s.setItem(`${KLUCZ_SAMONAPRAWY}-proba`, '1');
    return s;
  } catch {
    return null;
  }
}

/**
 * Czy wolno teraz przeładować stronę w ramach samonaprawy. `true` co najwyżej RAZ na build.
 *
 * @param build SHA buildu, który właśnie nie umiał wczytać modułu.
 */
export function czyMoznaPrzeladowac(build: string = BUILD_SHA, m: Magazyn | null = magazyn()): boolean {
  if (!m) return false;
  try {
    if (m.getItem(KLUCZ_SAMONAPRAWY) === build) return false;
    m.setItem(KLUCZ_SAMONAPRAWY, build);
    return true;
  } catch {
    return false;
  }
}

/**
 * Adres GŁÓWNEGO skryptu aplikacji, po którym poznajemy build.
 *
 * Nie porównujemy SHA z `BUILD_SHA`, bo tej wartości nie da się pobrać z serwera bez
 * osobnego pliku — a nazwa pliku wejściowego i tak zmienia się przy każdej zmianie kodu
 * (Vite hashuje zawartość). Jeśli serwer podaje inną nazwę niż ta, którą mamy załadowaną,
 * to jest inny build i tyle nam trzeba wiedzieć.
 */
export function adresGlownegoSkryptu(html: string): string | null {
  return html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;
}

export function zaladowanyGlownySkrypt(dokument: Document = document): string | null {
  const el = dokument.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  const src = el?.getAttribute('src') ?? null;
  return src ? adresGlownegoSkryptu(src) : null;
}

/** Czy serwer ma inny build niż ten w karcie. `null` = nie dało się stwierdzić. */
export async function czySerwerMaNowszyBuild(
  pobierz: (url: string) => Promise<Response> = (u) => fetch(u, { cache: 'no-store' }),
  dokument: Document = document,
): Promise<boolean | null> {
  const nasz = zaladowanyGlownySkrypt(dokument);
  if (!nasz) return null;
  try {
    const odp = await pobierz('/');
    if (!odp.ok) return null;
    const serwerowy = adresGlownegoSkryptu(await odp.text());
    if (!serwerowy) return null;
    return serwerowy !== nasz;
  } catch {
    return null;
  }
}

/**
 * ZATRUTA PAMIĘĆ PRZEGLĄDARKI (HOSTING-404, 16.10). Do 16.10 brakujący plik z `/assets/*`
 * dostawał od serwera index.html z nagłówkiem `immutable` na rok. Przeglądarka, która raz
 * trafiła na taki moment, trzyma HTML pod adresem kawałka aplikacji — samo przeładowanie go
 * nie rusza, bo `immutable` znaczy „nie pytaj serwera”. Serwer już odpowiada 404 `no-store`
 * (`src/lib/zasobyNaBrzegu.ts`), ale wpisy sprzed poprawki zostały.
 *
 * Dlatego przed przeładowaniem nadpisujemy zatrute wpisy pobraniem z `cache: 'reload'`: to
 * omija pamięć i zapisuje świeżą odpowiedź, więc przeładowana strona dostaje prawdziwy plik
 * (albo 404, którego nikt nie zapamięta). Adres znamy tylko z komunikatu błędu — Chrome i
 * Firefox go podają, Safari nie; wtedy zostaje samo przeładowanie, jak dotąd.
 */
export function adresBrakujacegoModulu(blad: unknown): string | null {
  const tekst =
    typeof blad === 'string' ? blad : String((blad as { message?: unknown } | null)?.message ?? '');
  const trafienie = tekst.match(/(?:https?:\/\/[^\s'"]+)?\/assets\/[^\s'"]+/)?.[0];
  if (!trafienie) return null;
  try {
    const url = new URL(trafienie, globalThis.location?.origin ?? 'http://localhost');
    if (globalThis.location && url.origin !== globalThis.location.origin) return null;
    return url.pathname.startsWith('/assets/') ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * Statyczne importy kawałka (`import … from "./x.js"`, `import "./x.js"`, `export … from`),
 * w postaci zminifikowanej i nie. Dynamiczne `import("./x.js")` pomijamy: to osobne leniwe
 * kawałki, które nie wywracają TEGO modułu.
 */
export function importyStatyczne(kod: string, adresModulu: string): string[] {
  const wynik = new Set<string>();
  for (const m of kod.matchAll(/(?:\bfrom|\bimport)\s*["'](\.{1,2}\/[^"'\s]+?\.js)["']/g)) {
    try {
      wynik.add(new URL(m[1], adresModulu).href);
    } catch {
      // nieparsowalny specyfikator — pomijamy
    }
  }
  return [...wynik];
}

type Pobierz = (url: string, init: RequestInit) => Promise<Response>;

const LIMIT_PLIKOW = 300;
const ROZMIAR_PACZKI = 8;

/**
 * Odtrucie CAŁEGO grafu, nie tylko modułu z komunikatu (zgłoszenie Maćka 19.10). Błąd
 * `Failed to fetch dynamically imported module` nazywa tylko moduł z GÓRY — np.
 * `ProfilePage-….js` — a zatruty bywa jego statyczny import (`door-open-….js`). Odświeżenie
 * samego ProfilePage nic nie dawało: każde przeładowanie padało tak samo.
 *
 * Idziemy więc wszerz po statycznych importach od wadliwego adresu. Każdy plik pobieramy
 * NORMALNIE (z pamięci — tanio); jeśli to nie JavaScript, to zatruty wpis i pobieramy go
 * jeszcze raz z `cache: 'reload'`. Jeśli JS — czytamy jego importy i idziemy dalej. Tylko
 * nasz origin i tylko `/assets/*.js`; zbiór odwiedzonych chroni przed cyklami, limit plików
 * przed nieskończonym grafem. Zwraca listę odświeżonych adresów (dla testów i logu).
 */
export async function odtrujGrafModulu(start: string, pobierz: Pobierz): Promise<string[]> {
  const origin = new URL(start).origin;
  const czyNasz = (u: string) => {
    const url = new URL(u);
    return url.origin === origin && url.pathname.startsWith('/assets/') && url.pathname.endsWith('.js');
  };
  const odwiedzone = new Set<string>([start]);
  const odswiezone: string[] = [];
  let kolejka = [start];

  const sprawdz = async (url: string): Promise<string[]> => {
    try {
      const odp = await pobierz(url, {});
      const typ = odp.headers.get('content-type') ?? '';
      if (!/javascript/i.test(typ)) {
        odswiezone.push(url);
        await pobierz(url, { cache: 'reload' }).catch(() => undefined);
        return [];
      }
      return importyStatyczne(await odp.text(), url);
    } catch {
      return [];
    }
  };

  while (kolejka.length) {
    const nastepna: string[] = [];
    for (let i = 0; i < kolejka.length; i += ROZMIAR_PACZKI) {
      const wyniki = await Promise.all(kolejka.slice(i, i + ROZMIAR_PACZKI).map(sprawdz));
      for (const dzieci of wyniki) {
        for (const d of dzieci) {
          if (odwiedzone.has(d) || odwiedzone.size >= LIMIT_PLIKOW || !czyNasz(d)) continue;
          odwiedzone.add(d);
          nastepna.push(d);
        }
      }
    }
    kolejka = nastepna;
  }
  return odswiezone;
}

const LIMIT_ODSWIEZENIA_MS = 3000;

export async function przeladujPoBledzieModulu(
  blad: unknown,
  przeladuj: () => void = () => window.location.reload(),
  pobierz: Pobierz = (u, i) => fetch(u, i),
): Promise<void> {
  const url = adresBrakujacegoModulu(blad);
  if (url) {
    // Odświeżenie jest najlepszą próbą: zawieszone łącze nie może zablokować przeładowania.
    await Promise.race([
      odtrujGrafModulu(url, pobierz).catch(() => undefined),
      new Promise((r) => setTimeout(r, LIMIT_ODSWIEZENIA_MS)),
    ]);
  }
  przeladuj();
}
